/**
 * "Add a Client" guided walkthrough.
 *
 * Walks the user through BOTH onboarding paths in sequence:
 *   1. Send Onboarding Form (email a self-service link)
 *   2. Onboard Manually — every step of the 7-step wizard
 *
 * Event-driven: each step advances when the user performs the actual action
 * the tooltip describes. No Back/Next buttons except on the welcome and outro
 * screens, which need them because there's no specific action to chain off.
 *
 * Wizard step transitions are detected via the data-tour-step-N elements
 * appearing/disappearing — this means the tour stays in sync with the wizard
 * even if validation blocks Continue (the next tooltip just waits until the
 * wizard actually advances).
 *
 * Targets used (data-tour attributes scattered through the components):
 *   data-tour="add-client-btn"       — "Add Client" button on the client list
 *   data-tour="add-choice-send"      — "Send Onboarding Form" card in choice modal
 *   data-tour="add-choice-manual"    — "Onboard Manually" card in choice modal
 *   data-tour="send-form-cancel"     — "Cancel" button on send-form modal
 *   data-tour="wizard-step-1..7"     — content area of each wizard step
 *   data-tour="wizard-continue"      — wizard "Continue" button (steps 1-6)
 *   data-tour="wizard-save"          — wizard "Save Client" button (step 7)
 */

// START + DONE come from the shared helpers so wording / behaviour stays
// in lockstep with every other tour (the DONE button, in particular,
// navigates back to the Help page on click).
import { START, DONE } from './_helpers.js';

// Resolves once the selector matches a DOM element. Polls every 50ms.
// Long timeout (10s) so wizard steps with validation have time to clear.
function waitForElement(selector, timeout = 10000) {
    return new Promise((resolve) => {
        if (document.querySelector(selector)) return resolve();
        const start = Date.now();
        const check = () => {
            if (document.querySelector(selector)) return resolve();
            if (Date.now() - start > timeout) return resolve();
            setTimeout(check, 50);
        };
        check();
    });
}

// Resolves once a currently-present selector has disappeared.
function waitForElementGone(selector, timeout = 10000) {
    return new Promise((resolve) => {
        if (!document.querySelector(selector)) return resolve();
        const start = Date.now();
        const check = () => {
            if (!document.querySelector(selector)) return resolve();
            if (Date.now() - start > timeout) return resolve();
            setTimeout(check, 50);
        };
        check();
    });
}

// Returns a Shepherd `when` hook that keeps the tour in lockstep with the
// onboarding wizard's current step. Used INSTEAD of `advanceOn: click` so:
//   - Required-field validation blocking Continue keeps the tooltip put.
//   - Clicking Previous in the wizard rewinds the tour to the matching step.
//   - On the final step, completing Save Client (which closes the wizard)
//     advances the tour to the outro.
//
// `myStepNum` is the wizard-step number this tour step represents (1–7).
// `isLast` flags step 7, which advances when the wizard modal closes
// rather than when the next step's marker appears.
function syncWithWizard(myStepNum, isLast = false) {
    return {
        show() {
            const tour = this.tour;
            const stepInstance = this;
            let observer = null;
            let poll = null;
            // Don't act on the very first check tick — give Shepherd a
            // beat to settle so we don't see transient DOM state.
            let armed = false;
            setTimeout(() => { armed = true; }, 50);

            const teardown = () => {
                if (observer) { observer.disconnect(); observer = null; }
                if (poll) { clearInterval(poll); poll = null; }
                stepInstance._advanceWatch = null;
            };

            const currentWizardStep = () => {
                for (let n = 1; n <= 7; n++) {
                    if (document.querySelector(`[data-tour="wizard-step-${n}"]`)) {
                        return n;
                    }
                }
                return null;
            };

            const check = () => {
                // Bail if the tour has moved on for any reason (cancelled,
                // user hit ×, etc.) so we don't fire a stray next() into
                // the void.
                let current = null;
                try { current = tour.getCurrentStep && tour.getCurrentStep(); }
                catch (e) { /* ignore */ }
                if (current !== stepInstance) {
                    teardown();
                    return;
                }
                if (!armed) return;

                const stepperGone = !document.querySelector('[data-tour="wizard-stepper"]');
                if (stepperGone) {
                    // Wizard modal has closed. On the last step that's our
                    // advance signal (Save Client succeeded). On earlier
                    // steps we leave it to `cancelIfMissing` to clean up.
                    if (isLast) {
                        teardown();
                        try { tour.next(); } catch (e) { /* ignore */ }
                    }
                    return;
                }

                const wizardStepNum = currentWizardStep();
                if (wizardStepNum === null) return; // transient, try again
                if (wizardStepNum === myStepNum) return; // in sync

                teardown();
                if (wizardStepNum > myStepNum) {
                    try { tour.next(); } catch (e) { /* ignore */ }
                } else {
                    try { tour.back(); } catch (e) { /* ignore */ }
                }
            };

            observer = new MutationObserver(check);
            observer.observe(document.body, { childList: true, subtree: true });
            // Belt-and-braces poll in case a mutation batch is coalesced
            // and the observer misses the change. Cheap (200ms tick).
            poll = setInterval(check, 200);
            stepInstance._advanceWatch = teardown;
        },
        hide() {
            if (this._advanceWatch) this._advanceWatch();
        },
    };
}

export const addClientTour = {
    id: 'add-client',
    title: 'Add a New Client',
    description: 'Walks through both ways to onboard a new client — sending an email onboarding link, then walking through every step of the manual onboarding wizard.',
    autoLaunchOn: { page: 'clients' },

    steps: [
        // ─── Intro ───────────────────────────────────────────────────────────
        {
            id: 'intro',
            title: 'Welcome — let\'s add a client',
            text: `This walkthrough covers both ways to onboard a new client:
                first, sending an <strong>email onboarding link</strong>; then walking through
                <strong>every step of the manual wizard</strong>.`,
            buttons: [START],
        },

        // ─── Open the choice modal ───────────────────────────────────────────
        {
            id: 'add-client-btn',
            attachTo: { element: '[data-tour="add-client-btn"]', on: 'bottom' },
            title: 'Click "Add Client"',
            text: 'Every new client starts here. Click <strong>Add Client</strong> to open the onboarding options.',
            advanceOn: { selector: '[data-tour="add-client-btn"]', event: 'click' },
        },

        // ─── Path 1: Send onboarding form ────────────────────────────────────
        {
            id: 'choose-send',
            attachTo: { element: '[data-tour="add-choice-send"]', on: 'right' },
            beforeShowPromise: () => waitForElement('[data-tour="add-choice-send"]'),
            title: 'Two ways to onboard',
            text: `<strong>Send Onboarding Form</strong> emails the client a self-service link
                they fill in remotely. <strong>Onboard Manually</strong> opens a wizard you fill
                in yourself.
                <br><br>
                Click <strong>Send Onboarding Form</strong> first — we'll cover the manual option after.`,
            advanceOn: { selector: '[data-tour="add-choice-send"]', event: 'click' },
        },
        {
            id: 'send-form',
            attachTo: { element: '[data-tour="send-form-cancel"]', on: 'top' },
            beforeShowPromise: () => waitForElement('[data-tour="send-form-cancel"]'),
            title: 'The Send Onboarding form',
            text: `Fill in the client's <strong>name</strong> and <strong>email</strong> (required),
                optionally assign a staff owner and add a note, then click
                <strong>Send Onboarding Link</strong> to email it. The client gets a unique link
                to a secure form — no login needed.
                <br><br>
                For this tour, click <strong>Cancel</strong> to continue.`,
            advanceOn: { selector: '[data-tour="send-form-cancel"]', event: 'click' },
        },

        // ─── Path 2: Manual onboarding wizard ────────────────────────────────
        {
            id: 'reopen-add-client',
            attachTo: { element: '[data-tour="add-client-btn"]', on: 'bottom' },
            beforeShowPromise: () => waitForElementGone('[data-tour="send-form-cancel"]'),
            title: 'Now the manual option',
            text: `Click <strong>Add Client</strong> again to reopen the choice menu.`,
            advanceOn: { selector: '[data-tour="add-client-btn"]', event: 'click' },
        },
        {
            id: 'choose-manual',
            attachTo: { element: '[data-tour="add-choice-manual"]', on: 'right' },
            beforeShowPromise: () => waitForElement('[data-tour="add-choice-manual"]'),
            title: 'Click "Onboard Manually"',
            text: `Use this when you have all the client's details to hand. Click
                <strong>Onboard Manually</strong> to open the wizard.`,
            advanceOn: { selector: '[data-tour="add-choice-manual"]', event: 'click' },
        },

        // ─── Wizard: 7 steps, one tooltip each ───────────────────────────────
        {
            id: 'wizard-step-1',
            attachTo: { element: '[data-tour="wizard-step-1"]', on: 'right' },
            beforeShowPromise: () => waitForElement('[data-tour="wizard-step-1"]'),
            cancelIfMissing: '[data-tour="wizard-stepper"]',
            title: 'Wizard Step 1 — Pre-commencement',
            text: `The wizard has 7 stages. This first one captures whether the
                client is a <strong>pre-commencement customer</strong> — an existing
                relationship from <em>before 31 March 2026</em> — because that
                changes everything that follows. Pre-commencement customers
                defer full verification until a suspicious matter arises or
                they request a service outside their regular scope.
                <br><br>
                Pick <strong>No</strong> for new engagements,
                <strong>Yes</strong> for existing customers, then click
                <strong>Continue</strong>.`,
            when: syncWithWizard(1),
        },
        {
            id: 'wizard-step-2',
            attachTo: { element: '[data-tour="wizard-step-2"]', on: 'right' },
            beforeShowPromise: () => waitForElement('[data-tour="wizard-step-2"]'),
            cancelIfMissing: '[data-tour="wizard-stepper"]',
            title: 'Wizard Step 2 — Designated Services',
            text: `Capture every <strong>designated service</strong> you'll provide
                to this client. The selection here drives the level of CDD
                required throughout onboarding.
                <br><br>
                Tick every service that applies (or "None"), then click
                <strong>Continue</strong> at the bottom.`,
            when: syncWithWizard(2),
        },
        {
            id: 'wizard-step-3',
            attachTo: { element: '[data-tour="wizard-step-3"]', on: 'right' },
            beforeShowPromise: () => waitForElement('[data-tour="wizard-step-3"]'),
            cancelIfMissing: '[data-tour="wizard-stepper"]',
            title: 'Wizard Step 3 — Client Type & Details',
            text: `Choose the client type — <strong>Individual</strong>,
                <strong>Body Corporate</strong>, <strong>Trust</strong>, <strong>SMSF</strong>,
                <strong>Partnership</strong>, or <strong>Other Unincorporated</strong>. The form
                fields below adapt to the type you select.
                <br><br>
                <strong>For companies</strong>, entering an ABN auto-populates the entity
                details from the ABR. Click <strong>Continue</strong> when the required fields
                are filled.`,
            when: syncWithWizard(3),
        },
        {
            id: 'wizard-step-4',
            attachTo: { element: '[data-tour="wizard-step-4"]', on: 'right' },
            beforeShowPromise: () => waitForElement('[data-tour="wizard-step-4"]'),
            cancelIfMissing: '[data-tour="wizard-stepper"]',
            title: 'Wizard Step 4 — Risk Assessment',
            text: `Answer each risk question to calculate the client's overall rating —
                <strong>Low</strong>, <strong>Medium</strong>, or <strong>High</strong>. The
                rating drives the review cycle and the verification path on the next step.
                <br><br>
                <strong>High-risk</strong> clients require Enhanced CDD and Senior Manager
                approval before any designated service can be provided. Click
                <strong>Continue</strong> when all questions are answered.`,
            when: syncWithWizard(4),
        },
        {
            id: 'wizard-step-5',
            attachTo: { element: '[data-tour="wizard-step-5"]', on: 'right' },
            beforeShowPromise: () => waitForElement('[data-tour="wizard-step-5"]'),
            cancelIfMissing: '[data-tour="wizard-stepper"]',
            title: 'Wizard Step 5 — Identity Verification',
            text: `Upload the client's identity documents to run electronic verification:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li><strong>Document Type</strong> (required) — pick from the
                        category groups (e.g. primary photographic, primary
                        non-photographic, entity registration). The list adapts to
                        the client type.</li>
                    <li><strong>Country of Issue</strong> — set this when the document
                        is issued outside Australia.</li>
                    <li><strong>Document images</strong> — drag or click to upload.
                        JPG / PNG / PDF, up to 10 MB per file.</li>
                    <li><strong>Secondary identification</strong> (individuals, optional)
                        — a second document slot for cases where the primary doc alone
                        isn't enough.</li>
                </ul>
                You don't need to record an outcome manually — the verification result
                comes back automatically and updates the client's CDD status. If you
                don't have the documents to hand right now, tick
                <strong>Skip ID verification for now</strong> and you can run it later
                from the client's CDD section.
                <br><br>
                Click <strong>Continue</strong> when ready.`,
            when: syncWithWizard(5),
        },
        {
            id: 'wizard-step-6',
            attachTo: { element: '[data-tour="wizard-step-6"]', on: 'right' },
            beforeShowPromise: () => waitForElement('[data-tour="wizard-step-6"]'),
            cancelIfMissing: '[data-tour="wizard-stepper"]',
            title: 'Wizard Step 6 — Associated Persons',
            text: `Add anyone connected to this client who needs to be on file —
                <strong>company officers</strong>, <strong>beneficial owners</strong>
                (typically &gt;25% interest), <strong>trustees</strong>, or anyone authorised
                to give instructions on the client's behalf.
                <br><br>
                For low-risk individual clients this step is usually optional. Click
                <strong>Continue</strong> to proceed to the final review.`,
            when: syncWithWizard(6),
        },
        {
            id: 'wizard-step-7',
            attachTo: { element: '[data-tour="wizard-step-7"]', on: 'top' },
            beforeShowPromise: () => waitForElement('[data-tour="wizard-step-7"]'),
            // No cancelIfMissing here — this step intentionally outlives
            // the wizard closing, because that's our advance signal.
            title: 'Wizard Step 7 — Review & Confirm',
            text: `A final summary of everything you've entered. Double-check the values
                before saving — this is your last chance to step back and adjust anything.
                <br><br>
                When you're satisfied, click <strong>Save Client</strong> at the bottom right
                to create the record. Any uploaded ID documents are saved at the same time.`,
            // Advance when the wizard modal actually closes (Save Client
            // succeeded), and rewind if the user clicks Previous back to
            // step 5. If save is blocked by a server-side error the wizard
            // stays open and the tour stays put — same behaviour we get
            // for the earlier steps when Continue is blocked.
            when: syncWithWizard(7, true),
        },

        // ─── Outro ───────────────────────────────────────────────────────────
        {
            id: 'finish',
            title: 'You\'re all set',
            text: `That's both ways to add a client, and every step of the manual wizard.
                You can replay this walkthrough — or any future ones — from the
                <strong>Help</strong> page in the sidebar.`,
            buttons: [DONE],
        },
    ],
};
